import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import { EnhancedClass } from '../../types/class-data';
import { loadAllClasses, findClassById } from '../../utils/classDataLoader';
import ContentText from '../Common/ContentText';
import './ClassSelection.css';

interface ClassSelectionProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

const ClassSelection: React.FC<ClassSelectionProps> = ({
  characterData,
  onDataChange,
}) => {
  const [classes, setClasses] = useState<EnhancedClass[]>([]);
  const [selectedClass, setSelectedClass] = useState<EnhancedClass | null>(null);
  // Note: selectedSubclass removed since subclass selection is now its own step
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadClassData = async () => {
      try {
        // Load all classes using the new loader
        const allClasses = await loadAllClasses();
        setClasses(allClasses);
        
        // If character already has a class selected, find and set it
        if (characterData.class) {
          const foundClass = await findClassById(characterData.class);
          if (foundClass) {
            setSelectedClass(foundClass);
          }
        }
      } catch (err) {
        console.error('Failed to load classes:', err);
        setError('Failed to load class data');
      } finally {
        setLoading(false);
      }
    };

    loadClassData();
  }, [characterData.class]);

  const handleClassSelect = (classData: EnhancedClass) => {
    setSelectedClass(classData);
    // Reset subclass when changing class since it's now a separate step
    onDataChange({ class: classData.id, subclass: '' });
  };

  if (loading) {
    return <div className="class-selection__loading">Loading classes...</div>;
  }

  if (error) {
    return <div className="class-selection__error">{error}</div>;
  }

  return (
    <div className="class-selection">
      <p className="class-selection__description">
        Your class determines your character's primary abilities, starting resources, and available 
        spellcasting domains. You'll also choose a subclass that specializes your character further.
      </p>

      <div className="class-selection__content">
        {/* Class List */}
        <div className="class-selection__list">
          <h3>Available Classes</h3>
          <div className="class-grid">
            {classes.map((classData) => (
              <div
                key={classData.id}
                className={`class-card ${
                  selectedClass?.id === classData.id ? 'class-card--selected' : ''
                }`}
                onClick={() => handleClassSelect(classData)}
              >
                <div className="class-card__name">{classData.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Class Details */}
        {selectedClass && (
          <div className="class-selection__details">
            <div className="class-details">
              <h3>{selectedClass.name}</h3>
              <ContentText 
                content={selectedClass.description}
                className="class-details__description"
              />

              <div className="class-details__info">
                <div className="info-section">
                  <h4>Starting Stats</h4>
                  <div className="stats-display">
                    <div className="stat-display">
                      <strong>Hit Points:</strong> {selectedClass.startingHitPoints}
                    </div>
                    <div className="stat-display">
                      <strong>Stress:</strong> {selectedClass.startingStress}
                    </div>
                    <div className="stat-display">
                      <strong>Evasion:</strong> {selectedClass.startingEvasion}
                    </div>
                    <div className="stat-display">
                      <strong>Domains:</strong> {selectedClass.domains.join(', ')}
                    </div>
                  </div>
                </div>

                <div className="info-section">
                  <h4>Hope Feature</h4>
                  <div className="feature-item">
                    <h5>{selectedClass.hopeFeature.name} ({selectedClass.hopeFeature.cost} Hope)</h5>
                    <ContentText content={selectedClass.hopeFeature.description} />
                  </div>
                </div>

                <div className="info-section">
                  <h4>Class Features</h4>
                  {selectedClass.classFeatures.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <h5>{feature.name}</h5>
                      <ContentText content={feature.description} />
                      {feature.uses && <small><strong>Uses:</strong> {feature.uses}</small>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassSelection;